import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";

import { BankLogo } from "@dfp/react-components";

import { makeScaInstructionNumber } from "../demoBankConnect.utils.ts";
import type { BankFlowProps, BankFlowStep } from "./bankFlow.types.ts";

// Replica of ČSOB's "ČSOB Identity" PSD2 portal (login → Smart klíč SCA →
// account consent). The copy is intentionally hard-coded Czech — these are
// external bank pages, not localized app UI.
const CSOB_BLUE = "#0F4DAE";
const CSOB_GREEN = "#009E60";
const CSOB_TEXT = "#333333";
const CSOB_BG = "#f0f2f5";
const CSOB_LINK = "#0F4DAE";
const CSOB_RED = "#D0021B";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

function CsobHeader() {
  return (
    <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e0e0e0" }}>
      <Container maxWidth="lg">
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <BankLogo
              bank="CSOB"
              style={{ height: 36, width: "auto", display: "block" }}
            />
            <Typography
              sx={{
                color: CSOB_BLUE,
                fontWeight: 800,
                fontSize: 26,
                letterSpacing: "0.02em",
                fontFamily: FONT_STACK,
              }}
            >
              ID
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ alignItems: "center", gap: 3 }}>
            <Typography
              sx={{ fontSize: 13.5, color: CSOB_TEXT, fontFamily: FONT_STACK }}
            >
              📱 Kontakty&nbsp;&nbsp;495 300 300
            </Typography>
            <Link
              sx={{
                color: CSOB_LINK,
                fontSize: 13.5,
                fontFamily: FONT_STACK,
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              EN
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function CsobFooter() {
  return (
    <Box sx={{ borderTop: "1px solid #e0e0e0", bgcolor: "#fff", mt: 6 }}>
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: "column", md: "row" }}
          sx={{
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 1,
            py: 2.5,
          }}
        >
          <Typography
            sx={{ fontSize: 12.5, color: CSOB_TEXT, fontFamily: FONT_STACK }}
          >
            © Československá obchodní banka, a. s.
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              color: CSOB_TEXT,
              fontFamily: FONT_STACK,
              textAlign: { xs: "left", md: "right" },
            }}
          >
            <Link sx={{ color: CSOB_LINK, cursor: "pointer" }}>
              Přejít na Úvodní stránku ČSOB
            </Link>
            {" | "}
            <Link sx={{ color: CSOB_LINK, cursor: "pointer" }}>
              Vstoupit do ČSOB CEB
            </Link>
            {" | "}
            <Link sx={{ color: CSOB_LINK, cursor: "pointer" }}>
              Vstoupit do IB s ID
            </Link>
            {" | "}
            <Link sx={{ color: CSOB_LINK, cursor: "pointer" }}>
              Obchodní podmínky
            </Link>
            {" | "}
            <Link sx={{ color: CSOB_LINK, cursor: "pointer" }}>
              Provozní informace
            </Link>
            {" | "}
            <Link sx={{ color: CSOB_LINK, cursor: "pointer" }}>
              Ochrana osobních údajů
            </Link>
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function CsobCard({ children, ...rest }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        p: 3,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** Faux QR code — a CSS checkerboard square standing in for the real image. */
function QrPlaceholder() {
  return (
    <Box
      sx={{
        width: 120,
        height: 120,
        border: "1px solid #c9c9c9",
        backgroundColor: "#fff",
        backgroundImage:
          "linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)",
        backgroundSize: "16px 16px",
        backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          px: 0.75,
          fontSize: 13,
          fontWeight: 700,
          color: CSOB_TEXT,
          fontFamily: FONT_STACK,
        }}
      >
        QR
      </Box>
    </Box>
  );
}

function StoreBadge({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: "#1a1a1a",
        color: "#fff",
        borderRadius: 1.5,
        px: 2,
        py: 0.75,
        fontSize: 12.5,
        fontWeight: 600,
        fontFamily: FONT_STACK,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

const csobInputSx = {
  bgcolor: "#fff",
  width: "100%",
  "& .MuiOutlinedInput-root": { borderRadius: 0.5 },
  "& input": { py: 1.1, px: 1.5, fontSize: 14, fontFamily: FONT_STACK },
};

const helpCircleSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: `1px solid ${CSOB_BLUE}`,
  color: CSOB_BLUE,
  fontSize: 11,
  fontWeight: 700,
  fontFamily: FONT_STACK,
};

/** ČSOB Identity PSD2 flow: login → Smart klíč SCA → account consent. */
export function CsobFlow({
  thirdPartyName,
  ownerName,
  username,
  connectionId,
  accounts,
  initialStep,
  onConnect,
  onCancel,
}: BankFlowProps) {
  const [step, setStep] = useState<BankFlowStep>(initialStep);
  const [loginName, setLoginName] = useState(username);
  const [password, setPassword] = useState("csob-demo-password");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  // The Smart klíč push approval "arrives" by itself after a moment, like the
  // real mobile-app confirmation would.
  useEffect(() => {
    if (step !== "sca") return;
    const timer = setTimeout(() => setStep("accounts"), 3000);
    return () => clearTimeout(timer);
  }, [step]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const greenButtonSx = {
    bgcolor: CSOB_GREEN,
    color: "#fff",
    borderRadius: 0.5,
    fontFamily: FONT_STACK,
    fontWeight: 700,
    px: 3,
    boxShadow: "none",
    "&:hover": { bgcolor: "#008451", boxShadow: "none" },
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: CSOB_BG, fontFamily: FONT_STACK }}>
      <CsobHeader />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {step === "login" && (
          <>
            <Stack
              direction="row"
              sx={{
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 26,
                  color: CSOB_TEXT,
                  fontFamily: FONT_STACK,
                }}
              >
                Přihlášení do portálu ČSOB Identity
              </Typography>
              <Link
                sx={{
                  color: CSOB_LINK,
                  fontSize: 14,
                  fontFamily: FONT_STACK,
                  cursor: "pointer",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                ❓ Nápověda
              </Link>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
                gap: 4,
              }}
            >
              {/* Left column */}
              <Stack sx={{ gap: 3 }}>
                <CsobCard>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                      mb: 1.5,
                    }}
                  >
                    Poskytovatel služeb požaduje vaši autentizaci
                    prostřednictvím ČSOB Identity.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    {thirdPartyName}
                  </Typography>
                </CsobCard>

                <CsobCard>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                      mb: 2,
                    }}
                  >
                    Bleskové přihlášení
                  </Typography>
                  <Stack direction="row" sx={{ gap: 2.5, alignItems: "flex-start" }}>
                    <QrPlaceholder />
                    <Stack sx={{ gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          color: CSOB_TEXT,
                          fontFamily: FONT_STACK,
                        }}
                      >
                        Máte internet v mobilu a nechcete vyplňovat uživatelské
                        jméno a heslo?
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          color: CSOB_TEXT,
                          fontFamily: FONT_STACK,
                        }}
                      >
                        1. Načtěte tento QR kód do Smart klíče (ikonka s QR kódem
                        v aplikaci)
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          color: CSOB_TEXT,
                          fontFamily: FONT_STACK,
                        }}
                      >
                        2. Potvrďte přihlášení a jste uvnitř
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: CSOB_TEXT,
                          fontFamily: FONT_STACK,
                          mt: 0.5,
                        }}
                      >
                        <strong>TIP:</strong> Jste na mobilu? Na QR kód klikněte
                        a do Smart klíče se vám načte sám.
                      </Typography>
                    </Stack>
                  </Stack>
                </CsobCard>

                <CsobCard>
                  <Stack
                    direction="row"
                    sx={{
                      gap: 3,
                      borderBottom: "1px solid #e0e0e0",
                      mb: 2.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: CSOB_BLUE,
                        fontFamily: FONT_STACK,
                        pb: 1,
                        borderBottom: `2px solid ${CSOB_BLUE}`,
                      }}
                    >
                      Heslo
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#888",
                        fontFamily: FONT_STACK,
                        pb: 1,
                        cursor: "pointer",
                      }}
                    >
                      Certifikát
                    </Typography>
                  </Stack>

                  <Stack sx={{ gap: 2, maxWidth: 360 }}>
                    <Box>
                      <Stack
                        direction="row"
                        sx={{ alignItems: "center", gap: 0.75, mb: 0.5 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: CSOB_TEXT,
                            fontFamily: FONT_STACK,
                          }}
                        >
                          Uživatelské jméno
                        </Typography>
                        <Box component="span" sx={helpCircleSx}>
                          ?
                        </Box>
                      </Stack>
                      <TextField
                        value={loginName}
                        onChange={(event) => setLoginName(event.target.value)}
                        sx={csobInputSx}
                      />
                    </Box>

                    <Box>
                      <Stack
                        direction="row"
                        sx={{ alignItems: "center", gap: 0.75, mb: 0.5 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: CSOB_TEXT,
                            fontFamily: FONT_STACK,
                          }}
                        >
                          Heslo
                        </Typography>
                        <Box component="span" sx={helpCircleSx}>
                          ?
                        </Box>
                      </Stack>
                      <TextField
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        sx={csobInputSx}
                      />
                    </Box>

                    <Link
                      sx={{
                        color: CSOB_LINK,
                        fontSize: 13.5,
                        fontFamily: FONT_STACK,
                        cursor: "pointer",
                      }}
                    >
                      Nedaří se mi přihlásit
                    </Link>

                    <Box>
                      <Button
                        variant="contained"
                        onClick={() => setStep("sca")}
                        disabled={!loginName || !password}
                        sx={greenButtonSx}
                      >
                        Přihlásit
                      </Button>
                    </Box>
                  </Stack>
                </CsobCard>
              </Stack>

              {/* Right column */}
              <Stack sx={{ gap: 3 }}>
                <CsobCard>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                      mb: 1.5,
                    }}
                  >
                    Více o ČSOB Identitě a co všechno umí najdete na našich
                    stránkách.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    Pokud ještě ČSOB Identitu nemáte, sjednejte si ji v
                    internetovém bankovnictví s ID nebo na našich obchodních
                    místech.
                  </Typography>
                </CsobCard>

                <Box
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #e0e0e0",
                    borderTop: `3px solid ${CSOB_RED}`,
                    borderRadius: 1,
                    p: 3,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                      mb: 1.5,
                    }}
                  >
                    Pamatujte na bezpečnost:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                      mb: 1.5,
                    }}
                  >
                    Kyberzločinci zneužívají internetové vyhledávače a nabízí
                    falešné přihlašovací weby.{" "}
                    <Link sx={{ color: CSOB_LINK, cursor: "pointer" }}>
                      Zjistit více
                    </Link>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                    }}
                  >
                    Hlášení internetových podvodů 499 900 010
                  </Typography>
                </Box>

                <CsobCard>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: CSOB_TEXT,
                      fontFamily: FONT_STACK,
                      textAlign: "center",
                      mb: 2,
                    }}
                  >
                    Stáhněte si mobilní aplikaci Smart klíč
                  </Typography>
                  <Stack
                    direction="row"
                    sx={{
                      gap: 1,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <StoreBadge>App Store</StoreBadge>
                    <StoreBadge>Google Play</StoreBadge>
                    <StoreBadge>AppGallery</StoreBadge>
                  </Stack>
                </CsobCard>
              </Stack>
            </Box>
          </>
        )}

        {step === "sca" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CsobCard>
              <Stack sx={{ alignItems: "center", gap: 2.5, maxWidth: 420 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 20,
                    color: CSOB_TEXT,
                    fontFamily: FONT_STACK,
                    textAlign: "center",
                  }}
                >
                  Potvrďte přihlášení v aplikaci Smart klíč
                </Typography>
                <CircularProgress sx={{ color: CSOB_GREEN }} />
                <Typography
                  sx={{
                    fontSize: 14,
                    color: CSOB_TEXT,
                    fontFamily: FONT_STACK,
                    textAlign: "center",
                  }}
                >
                  Autorizujte pokyn č.{" "}
                  <strong>{makeScaInstructionNumber(connectionId)}</strong> v
                  aplikaci Smart klíč.
                </Typography>
              </Stack>
            </CsobCard>
          </Box>
        )}

        {step === "accounts" && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "100%", maxWidth: 640 }}>
              <CsobCard>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 22,
                    color: CSOB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 1.5,
                  }}
                >
                  Souhlas s předáním údajů
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: CSOB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 3,
                  }}
                >
                  Potvrzujete, že služba <strong>{thirdPartyName}</strong> získá
                  přístup k Vašim údajům.
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: CSOB_TEXT,
                    fontFamily: FONT_STACK,
                    mb: 1.5,
                  }}
                >
                  Účty, ke kterým povolím přístup
                </Typography>

                <Stack data-tour="bank-accounts" sx={{ gap: 0.5, mb: 3 }}>
                  {accounts.map((account) => (
                    <Stack
                      key={account.id}
                      direction="row"
                      onClick={() => toggle(account.id)}
                      sx={{ alignItems: "center", gap: 1, cursor: "pointer" }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(account.id)}
                        sx={{
                          p: 0.5,
                          color: CSOB_GREEN,
                          "&.Mui-checked": { color: CSOB_GREEN },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: CSOB_TEXT,
                          fontFamily: FONT_STACK,
                        }}
                      >
                        {ownerName} ({account.accountNumber} - {account.currency}
                        )
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
                  <Button
                    data-tour="bank-connect-submit"
                    variant="contained"
                    disabled={selectedIds.size === 0}
                    onClick={() =>
                      onConnect(
                        accounts.filter((account) =>
                          selectedIds.has(account.id),
                        ),
                      )
                    }
                    sx={greenButtonSx}
                  >
                    Potvrdit
                  </Button>
                  <Link
                    onClick={onCancel}
                    sx={{
                      color: CSOB_RED,
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: FONT_STACK,
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    Zrušit
                  </Link>
                </Stack>
              </CsobCard>
            </Box>
          </Box>
        )}
      </Container>

      <CsobFooter />
    </Box>
  );
}
